import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { analytics, safePage, track, trackItems, purchasePayload, CONSENT_KEY } from './analytics.js';

// Lives inside both providers. Auth claims are resolved before any measurement.
export default function AnalyticsBridge({ state }) {
    const location = useLocation();
    const { currentUser, loading, getIdTokenClaims } = useAuth();
    const [identity, setIdentity] = useState({ user: null, allowed: false });
    const [consent, setConsent] = useState(() => analytics?.consent());
    const [open, setOpen] = useState(false);
    useEffect(() => { const show = () => setOpen(true); window.addEventListener('btl-cookie-choices', show); return () => window.removeEventListener('btl-cookie-choices', show); }, []);
    const path = safePage(location.pathname, state.packs);
    const allowed = !loading && identity.user === currentUser && identity.allowed;
    useEffect(() => {
        const refresh = e => {
            if (e && e.key !== CONSENT_KEY && e.key !== null) return;
            analytics?.refreshConsent();
            setConsent(analytics?.consent());
        };
        window.addEventListener('storage', refresh);
        return () => window.removeEventListener('storage', refresh);
    }, []);
    useEffect(() => {
        let live = true;
        if (loading) return;
        if (!currentUser) { setIdentity({ user: currentUser, allowed: true }); return; }
        getIdTokenClaims().then(claims => {
            if (live) setIdentity({ user: currentUser, allowed: !!claims && claims.role !== 'admin' });
        }).catch(() => { if (live) setIdentity({ user: currentUser, allowed: false }); });
        return () => { live = false; };
    }, [currentUser, loading, getIdTokenClaims]);

    useEffect(() => {
        try { analytics?.setContext(path, allowed); window.dispatchEvent(new Event('btl-analytics-ready')); } catch { /* Optional measurement only. */ }
        return () => { try { analytics?.setContext(null, false); } catch { /* No-op. */ } };
    }, [path, allowed, consent]);

    useEffect(() => {
        if (!allowed || consent?.choice !== 'granted') return;
        const key = location.key + ':' + path;
        if (path === '/cart' && state.cart.length) trackItems('view_cart', state.cart, {}, key);
        if (path === '/checkout' && state.cart.length) trackItems('begin_checkout', state.cart, {}, key);
        const pack = state.packs.find(p => '/preview/' + p.id === path);
        if (pack) trackItems('view_item', [pack], {}, key);
    // Event once per navigation/consent, not per background catalogue update.
    }, [path, location.key, allowed, consent]);

    useEffect(() => {
        const click = e => {
            const a = e.target.closest?.('a[href]');
            if (!a) return;
            if (a.getAttribute('href') === 'mailto:learn@daniellenglish.com') track('contact_click', { contact_type: 'email' });
            if (a.hostname === 'www.instagram.com' || a.hostname === 'instagram.com') track('contact_click', { contact_type: 'instagram' });
        };
        const scroll = () => {
            const h = document.documentElement.scrollHeight;
            if (h > window.innerHeight && window.scrollY + window.innerHeight >= h * 0.9)
                track('scroll', { percent_scrolled: 90 }, 'scroll:' + location.key + ':' + path);
        };
        document.addEventListener('click', click);
        window.addEventListener('scroll', scroll, { passive: true });
        return () => { document.removeEventListener('click', click); window.removeEventListener('scroll', scroll); };
    }, [path, location.key]);

    // Browser-side purchases: only server-confirmed paid transactions created
    // AFTER opt-in. No success-URL inference, no backfill of old customer orders.
    // Manual approval is measured when the consenting buyer next opens the site.
    useEffect(() => {
        if (!db || !currentUser?.uid || !allowed || consent?.choice !== 'granted' || !analytics?.active()) return;
        let live = true;
        const inFlight = new Set();
        return (() => {
            const unsub = onSnapshot(query(collection(db, 'transactions'), where('studentUid', '==', currentUser.uid)), snap => {
                for (const doc of snap.docs) {
                    const txn = doc.data();
                    if (txn.status !== 'paid' || txn.createdAt?.toMillis?.() < consent.at || !txn.createdAt?.toMillis?.()) continue;
                    if (inFlight.has(doc.id)) continue;
                    inFlight.add(doc.id);
                    (async () => {
                        // Opaque purchase ID for deduplication; never send Stripe IDs or user IDs.
                        const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode('btl-purchase:' + doc.id));
                        const id = 'btl_' + Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('');
                        const key = 'btl_ga_purchase_' + id;
                        try { if (localStorage.getItem(key)) return; } catch { /* Memory/GA dedupe still apply. */ }
                        const lines = await getDocs(collection(db, 'transactions', doc.id, 'lineItems'));
                        const payload = purchasePayload(txn, lines.docs.map(d => d.data()), id);
                        if (live && payload && track('purchase', payload, id)) {
                            try { localStorage.setItem(key, '1'); } catch { /* Best effort. */ }
                        }
                    })().catch(() => { inFlight.delete(doc.id); });
                }
            }, () => { /* Analytics is optional; never alter payment/access behavior. */ });
            return () => { live = false; unsub(); };
        })();
    }, [currentUser, allowed, consent, path]);

    function choose(choice, advertising = false) {
        try { analytics?.choose(choice, advertising); setConsent(analytics?.consent()); } catch { /* Storage disabled. */ }
        setOpen(false);
    }
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/__dev')) return null;
    const show = !consent || open;
    return <>
        {show ? <aside aria-label="Cookie choices" className="fixed bottom-0 inset-x-0 z-[100] border-t border-book-accent/30 bg-book-page text-book-text shadow-book px-5 py-4">
            <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
                <div className="max-w-xl">
                    <h2 className="font-display text-base mb-1">A small note on cookies</h2>
                    <p className="font-serif text-sm leading-relaxed">Essential cookies keep your account working. Optional Google Analytics measures visits and purchases. Ad measurement also connects Google ad clicks to purchases; it does not enable personalised ads. We do not send your name, email, essays or chats. Change your choice any time. <a className="underline" href="/legal#analytics">Details</a></p>
                </div>
                <div className="flex flex-wrap gap-2 lg:max-w-sm">
                    <button className="border border-book-accent rounded-full px-4 py-2 text-sm" onClick={() => choose('denied')}>Essential only</button>
                    <button className="border border-book-accent rounded-full px-4 py-2 text-sm" onClick={() => choose('granted')}>Allow analytics</button>
                    <button className="border border-book-accent rounded-full px-4 py-2 text-sm" onClick={() => choose('granted', true)}>Analytics + ad measurement</button>
                </div>
            </div>
        </aside> : null}
    </>;
}
